import { PostEditingDetails } from "@/pages/Posts/PostEditor/PostEditor/postEditorConstants";
import { UseQueryResult } from "@tanstack/react-query";
import { ItemType } from "antd/es/menu/interface";
import { MenuProps } from "antd/lib";
import useAppContext from "config/AppContext/useAppContext";
import useUserPermissions from "config/UserPermissionsContext/useUserPermissions";
import { isRTLLanguage } from "fixtures/languages";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { MdContentCopy, MdOpenInNew, MdOutlineDownload } from "react-icons/md";
import { useAppSelector } from "store";
import { Post } from "store/posts/postConstants";
import { selectUserInfo } from "store/user/userSlice";
import { useGetPostRenderInfo } from "../../utils/useGetPostRenderInfo";
import useUpdatePostActions from "../../utils/useUpdatePostActions";
import usePostCardActions from "./usePostCardActions";

interface UsePostCardMenuProps {
  post: Post;
  postDetail: PostEditingDetails;
  isDownloading: boolean;
  setIsDownloading: Dispatch<SetStateAction<boolean>>;
  isFetchingPostDetails: boolean;
  refetch: () => Promise<UseQueryResult<PostEditingDetails>>;
  toggleFlip: () => void;
}

export const usePostCardMenu = ({
  post,
  postDetail,
  isDownloading,
  setIsDownloading,
  isFetchingPostDetails,
  refetch,
  toggleFlip,
}: UsePostCardMenuProps) => {
  const { realm } = useAppContext();
  const user = useAppSelector(selectUserInfo);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isFetchingForMenu, setIsFetchingForMenu] = useState(false);
  const { hasPermission } = useUserPermissions();

  const { loading: updatePostLoading, duplicatePost } = useUpdatePostActions();
  const { isCarousel, isVideo } = useGetPostRenderInfo(post);

  const postCardActions = usePostCardActions({
    post,
    postDetail,
    isCarousel,
    isVideo,
    refetch,
    setIsDownloading,
    toggleFlip,
    setIsFetchingForMenu,
    setIsMenuOpen,
    duplicatePost,
  });

  const { handleMenuClick, handleMenuOpenChange } = postCardActions;

  const isMenuLoading = Boolean(
    isDownloading ||
      updatePostLoading ||
      isFetchingPostDetails ||
      isFetchingForMenu
  );

  const menuItems: MenuProps["items"] = useMemo(() => {
    const items: ItemType[] = [];

    if (realm.isMarky) {
      if (
        hasPermission("post_status_draft_write") &&
        ["PUBLISHED", "LIKED"].includes(post.status)
      ) {
        items.push({
          key: "duplicate",
          label: "Duplicate post",
          icon: <MdContentCopy size={18} />,
        });
      }

      items.push(
        {
          key: "download",
          label: `Download ${isVideo ? "Video" : `Image${isCarousel ? "s" : ""}`}`,
          icon: <MdOutlineDownload size={18} />,
        }
        // {
        //   key: "feedback",
        //   label: "Leave feedback",
        //   icon: <MdChatBubbleOutline size={18} />,
        // }
      );
    }

    if (user.is_superuser) {
      const hasMedia = postDetail.mediaUrls && postDetail.mediaUrls.length > 0;
      (postDetail.inputs || !hasMedia) &&
        items.push({
          key: "go-to-template",
          label: "Go to template",
          icon: <MdOpenInNew size={18} />,
        });
    }

    return items;
  }, [
    postDetail.mediaUrls,
    postDetail.inputs,
    postDetail.template?.id,
    post.status,
    realm.isMarky,
    user.is_superuser,
    isVideo,
    isCarousel,
    isFetchingForMenu,
    updatePostLoading,
    hasPermission,
  ]);

  const isRTL = useMemo(
    () => isRTLLanguage(post.caption || ""),
    [post.caption]
  );

  return {
    isMenuOpen,
    isMenuLoading,
    menuItems,
    handleMenuClick,
    handleMenuOpenChange,
    isCarousel,
    isRTL,
  };
};
